import React, { useRef, useState } from "react";
import {
  generateServicesWithIA,
  generateServicesWithIAFromFiles,
  bulkCreateServices,
  updateOnboardingStep,
} from "../components/dataProvider";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Card,
  CardMedia,
  CardActions,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useRefresh } from "react-admin";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import FakeServiceRow from "./FakeServiceRow";
import { useContext } from "react";
import { OnboardingContext } from "../contexts/OnboardingContext";

const DEFAULT_IMAGE =
  "https://te-ayudo-img.s3.us-east-2.amazonaws.com/test/users/683a58551632fe88f64db2e2@1756357057194";
// https://te-ayudo-img.s3.us-east-2.amazonaws.com/test/users/683a58551632fe88f64db2e2@1756357057194

const CreateWithIAModal = ({ onClose }) => {
  const [images, setImages] = useState([]);
  const [files, setFiles] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const imageInputRef = useRef();
  const fileInputRef = useRef();
  const [saving, setSaving] = useState(false);
  const { setOnboardingSteps, checkAndTriggerOnboarding, onboardingSteps } = useContext(OnboardingContext);
  const refresh = useRefresh(); // Hook para refrescar el listado

  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB en bytes

  // Procesar imágenes con IA
  const handleProcessWithIA = async () => {
    setError("");
    setLoading(true);
    try {
      let iaServices = [];

      // Procesar imágenes si hay alguna
      if (images.length > 0) {
        const imageFiles = images.map((img) => img.file);
        const imageServices = await generateServicesWithIA(imageFiles);
        iaServices = [...iaServices, ...imageServices];
      }

      // Procesar archivos si hay alguno
      if (files.length > 0) {
        const documentFiles = files.map((file) => file.file);
        const fileServices = await generateServicesWithIAFromFiles(documentFiles);
        iaServices = [...iaServices, ...fileServices];
      }

      // Asignar un id único a cada servicio generado para edición/eliminación
      setServices(
        iaServices.map((s, idx) => ({
          ...s,
          id: idx + 1,
        }))
      );
    } catch (e) {
      setError("Ocurrió un error al procesar los archivos con IA");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const imageFiles = Array.from(e.target.files);
    const previews = imageFiles.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      type: "image",
    }));
    // Agrega las nuevas imágenes a las existentes
    setImages((prev) => [...prev, ...previews]);
  };

  const handleFileChange = (e) => {
    const documentFiles = Array.from(e.target.files);

    // Solo permitir un archivo
    if (documentFiles.length > 1) {
      setError("Solo puedes subir un archivo a la vez");
      return;
    }

    const file = documentFiles[0];
    if (!file) return;

    // Validar tamaño del archivo
    if (file.size > MAX_FILE_SIZE) {
      setError(`El archivo ${file.name} es demasiado grande. El tamaño máximo permitido es 20MB.`);
      return;
    }

    const supportedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/plain",
      "text/csv",
    ];

    if (!supportedTypes.includes(file.type)) {
      setError("Tipo de archivo no compatible. Solo se admiten: PDF, DOCX, XLSX, XLS, TXT, CSV");
      return;
    }

    const fileData = {
      file,
      name: file.name,
      size: file.size,
      type: "document",
    };

    // Reemplazar el archivo anterior (solo uno permitido)
    setFiles([fileData]);
    setError(""); // Limpiar errores si todo está bien
  };

  const handleRemoveImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    // Limpiar errores cuando se elimine el archivo
    setError("");
    // Resetear el input para permitir seleccionar el mismo archivo de nuevo
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleServiceChange = (id, newService) => {
    setServices((prev) => prev.map((s) => (s.id === id ? newService : s)));
  };

  const handleServiceDelete = (id) => {
    setServices((prev) => prev.filter((s) => s.id !== id));
  };

  // Guardar servicios masivos
  const handleSaveServices = async () => {
    setError("");
    setSaving(true);
    try {
      // Obtener datos de provider, category, subCategory, package, pinned si aplica
      const profile = JSON.parse(localStorage.getItem("profile"));
      const provider = profile?.role === "admin-sucursal" ? profile?.employer?._id : profile?._id;
      // Aquí puedes obtener category, subCategory, package, pinned según tu lógica/contexto
      // Por ahora los dejamos en null
      await bulkCreateServices({
        services: services.map((s) => ({
          name: s.name,
          description: s.description,
          price: s.price,
          image: s.image,
        })),
        provider,
        category: null,
        subCategory: null,
        package: null,
        pinned: null,
      });
      const userId = profile?._id;
      // const resp = await updateOnboardingStep({ userId: userId, step: 3 });
      // setOnboardingSteps(resp.data ?? []);
      await updateOnboardingStep(
        { userId: userId, step: 3 },
        { setOnboardingSteps, checkAndTriggerOnboarding, onboardingSteps }
      );
      refresh();

      // Limpiar y cerrar modal
      setServices([]);
      setError("");
      onClose && onClose();
    } catch (e) {
      setError("Ocurrió un error al guardar los servicios");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, position: "relative" }}>
      {/* Header visual */}
      <Box
        sx={{
          width: "calc(100% + 16px)",
          position: "relative",
          left: "-24px",
          top: "-24px",
          p: 0,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          background: "#252526",
          display: "flex",
          alignItems: "center",
          gap: 2,
          minHeight: 90,
          pl: 3,
          pr: 2,
          py: 2,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 38, color: "white" }} />
        <Box>
          <Typography variant="h5" fontWeight="bold" color="white" mb={0.5}>
            Crear Servicios con IA
          </Typography>
          <Typography variant="body2" color="white">
            Sube imágenes de tus productos/servicios o archivos como PDF, Word, Excel con información de tus servicios.
            La IA generará automáticamente los servicios para que puedas editarlos, eliminarlos o agregarlos.
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8, color: "grey.600" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          component="label"
          disabled={files.length > 0 || loading} // Bloquear si hay archivos cargados
          sx={{ borderRadius: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          <ImageIcon />
          Seleccionar Imágenes
          <input ref={imageInputRef} type="file" accept="image/*" multiple hidden onChange={handleImageChange} />
        </Button>

        <Button
          variant="outlined"
          component="label"
          disabled={images.length > 0 || loading} // Bloquear si hay imágenes cargadas
          sx={{ borderRadius: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          <DescriptionIcon />
          Seleccionar Archivo
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.xlsx,.xls,.txt,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/plain,text/csv"
            hidden
            onChange={handleFileChange}
          />
        </Button>

        <Button
          variant="contained"
          color="primary"
          sx={{ borderRadius: 2, display: "flex", alignItems: "center", gap: 1 }}
          onClick={handleProcessWithIA}
          disabled={(images.length === 0 && files.length === 0) || loading}
        >
          {loading && <CircularProgress size={18} color="inherit" />}
          {loading ? "Procesando..." : "Procesar con IA"}
        </Button>
      </Box>

      {/* Advertencia sutil sobre el tamaño máximo */}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
          💡 Tamaño máximo de archivos: 20MB. Las imágenes pueden ser múltiples, pero solo un archivo de documento a la
          vez.
        </Typography>
      </Box>

      <Grid container spacing={2} mb={3}>
        {images.map((img, idx) => (
          <Grid item key={idx} xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ width: "100%", aspectRatio: "1/1", position: "relative", borderRadius: 3, boxShadow: 2 }}>
              <CardMedia
                component="img"
                image={img.url}
                alt={`preview-${idx}`}
                sx={{ objectFit: "cover", width: "100%", height: 140, borderRadius: 3 }}
              />
              <CardActions sx={{ p: 0, justifyContent: "center", position: "absolute", top: 2, right: 2 }}>
                <Tooltip title="Eliminar imagen">
                  <IconButton size="small" onClick={() => handleRemoveImage(idx)} sx={{ background: "#fff" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Mostrar archivos seleccionados */}
      {files.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" mb={1} fontWeight="bold">
            Archivos seleccionados:
          </Typography>
          <Grid container spacing={1}>
            {files.map((file, idx) => (
              <Grid item key={idx} xs={12} sm={6} md={4}>
                <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, borderRadius: 2 }}>
                  <DescriptionIcon color="primary" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={file.name}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                  <Tooltip title="Eliminar archivo">
                    <IconButton size="small" onClick={() => handleRemoveFile(idx)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      {error && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: error.includes("demasiado grande") ? "#ffebee" : "#fff3e0",
            border: error.includes("demasiado grande") ? "1px solid #f44336" : "1px solid #ff9800",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            color={error.includes("demasiado grande") ? "error" : "warning.main"}
            variant="body2"
            sx={{ fontWeight: 500 }}
          >
            {error.includes("demasiado grande") ? "⚠️" : "ℹ️"} {error}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Vista previa de servicios generados por IA
        </Typography>
        <Button
          variant="contained"
          color="success"
          onClick={handleSaveServices}
          disabled={services.length === 0 || saving}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          {saving && <CircularProgress size={18} color="inherit" />}
          {saving ? "Guardando..." : "Guardar Servicios"}
        </Button>
      </Box>
      <Box>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 300,
              textAlign: "center",
              p: 4,
            }}
          >
            <CircularProgress size={60} sx={{ mb: 3, color: "primary.main" }} />
            <Typography variant="h6" fontWeight="bold" mb={1}>
              Procesando tus datos con IA
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Estamos analizando {files.length > 0 ? "tu archivo" : "tus imágenes"} para generar servicios
              automáticamente.
              <br />
              Este proceso puede tomar algunos momentos...
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AutoAwesomeIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography variant="caption" color="primary.main" fontWeight="bold">
                Inteligencia Artificial trabajando
              </Typography>
            </Box>
          </Box>
        ) : services.length === 0 ? (
          <Typography color="text.secondary" mb={2}>
            No hay servicios generados.
          </Typography>
        ) : (
          services.map((service) => (
            <FakeServiceRow
              key={service.id}
              service={{ ...service, image: service.image || DEFAULT_IMAGE }}
              onChange={handleServiceChange}
              onDelete={handleServiceDelete}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default CreateWithIAModal;
